import { useCallback, useEffect, useState } from "react";
import {
  Text,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TableBody,
  TableCell,
  UIPage,
  Container,
  PageHeader,
  useAuth,
  Button,
  Pagination,
  Empty,
  TableCellTitle,
  ButtonGroup,
} from "@eventsquare/uikit";
import { FilterBar, FilterSearch } from "@eventsquare/uikit/filters";
import {
  RiCoupon2Line,
  RiExternalLinkLine,
  RiFileList2Line,
  RiPrinterLine,
} from "@remixicon/react";
import { useTranslation } from "react-i18next";

import { formatCurrency, formatDateTime, getUserName } from "@lib/helpers";
import { Api } from "@lib/api";

import { Order, OrderCustomer } from "@/types/order";

import { PageLoader } from "@components/PageLoader/PageLoader";
import { NotesModal } from "@components/modals/NotesModal/NotesModal";
import { usePrint } from "@/hooks/usePrint";

interface FetchParams {
  search?: string;
  limit?: number;
  page?: number;
}

const ORDERS_PER_PAGE = 25;

export const Orders = () => {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [orderSearch, setOrderSearch] = useState<string>("");
  const [totalOrders, setTotalOrders] = useState<number | undefined>(undefined);
  const [orderWithNotes, setOrderWithNotes] = useState<Order | null>(null);

  const { hasPrinter, printPocket } = usePrint();
  const { accountId, user } = useAuth();
  const { t } = useTranslation("pages_orders");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: FetchParams = {
        //search: "test",
        limit: ORDERS_PER_PAGE,
        page: activePage,
      };

      if (orderSearch.trim().length > 0) {
        params.search = orderSearch.trim();
      }

      const data = await Api.get(`/accounts/${accountId}/pos/orders`, params);
      setOrders(data.orders);
      setTotalOrders(data.meta.pagination.total);
      setLoading(false);
    } catch (error) {
      if (Api.isApiError(error)) {
        //console.log(error.response);
      }
    }
  }, [accountId, activePage, orderSearch]);

  const printTickets = useCallback(
    async (url: string) => {
      const pocketId = url.split("/").slice(-1)[0];
      await printPocket(pocketId);
    },
    [printPocket],
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setOrderSearch(search);
      setActivePage(1);
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [search]);

  const getCustomerName = (customer: OrderCustomer) => {
    if (customer?.firstname && customer?.lastname) {
      return `${customer.firstname} ${customer.lastname}`;
    }
    if (customer?.firstname) {
      return customer.firstname;
    }
    if (customer?.lastname) {
      return customer.lastname;
    }
    if (customer?.email) {
      return customer.email;
    }
    return null;
  };

  const openTickets = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <UIPage>
      <PageHeader
        title={t("title")}
        badge={totalOrders || undefined}
        filters={
          <FilterBar>
            <FilterSearch
              value={search}
              setValue={setSearch}
              placeholder={t("filter.search")}
            />
          </FilterBar>
        }
      />
      <Container>
        {loading && <PageLoader />}
        {!loading && orders && orders.length === 0 && (
          <Empty
            icon={<RiCoupon2Line />}
            title={t("empty.title")}
            lead={t("empty.lead")}
          />
        )}
        {!loading && orders && orders.length > 0 && (
          <>
            <Table scrollable>
              <TableHead>
                <TableRow>
                  <TableHeadCell>{t("reference")}</TableHeadCell>
                  <TableHeadCell>{t("event")}</TableHeadCell>
                  <TableHeadCell>{t("price")}</TableHeadCell>
                  <TableHeadCell>{t("date")}</TableHeadCell>
                  <TableHeadCell></TableHeadCell>
                  <TableHeadCell></TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  return (
                    <TableRow key={order.id}>
                      <TableCell noWrap>
                        <Text variant="h6" noMargin>
                          <a href={order.pocket_url} target="_blank">
                            <TableCellTitle>{order.reference}</TableCellTitle>
                          </a>
                        </Text>
                        <Text variant="small" noMargin>
                          {getCustomerName(order.customer)}
                        </Text>
                      </TableCell>
                      <TableCell noWrap>
                        <Text variant="p" component="span" noMargin>
                          {`${order.event.name}${
                            order.edition.name ? ` - ${order.edition.name}` : ""
                          }`}
                        </Text>
                      </TableCell>
                      <TableCell noWrap>
                        <Text variant="p" component="span" noMargin>
                          {formatCurrency(order.price, order.currency)}
                        </Text>
                      </TableCell>
                      <TableCell noWrap>
                        <Text variant="p" noMargin>
                          {order.processed_at
                            ? formatDateTime(
                                order.processed_at,
                                user?.language ?? "nl",
                              )
                            : "-"}
                        </Text>
                        <Text variant="small" noMargin>
                          {getUserName(order.user)}
                        </Text>
                      </TableCell>
                      <TableCell noWrap>
                        {!!order.cart && order.cart.notes?.length >= 1 && (
                          <Button
                            icon
                            variant="outline"
                            size="tiny"
                            color="primary"
                            onClick={(e) => {
                              if (e) e.stopPropagation();
                              setOrderWithNotes(order);
                            }}
                          >
                            <RiFileList2Line />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell noWrap textAlign="right">
                        <ButtonGroup justify="end" inline>
                          {order.pocket_url && hasPrinter && (
                            <Button
                              variant="outline"
                              size="tiny"
                              icon
                              onClick={() => printTickets(order.pocket_url)}
                            >
                              <RiPrinterLine />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="tiny"
                            onClick={() => openTickets(order.pocket_url)}
                          >
                            <RiExternalLinkLine />
                            {t("tickets")}
                          </Button>
                        </ButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              activePage={activePage}
              itemsTotal={totalOrders ?? 0}
              itemsPerPage={ORDERS_PER_PAGE}
              setActivePage={setActivePage}
              translations={{
                back: t("pagination.back"),
                next: t("pagination.next"),
                active_page: t("pagination.active_page"),
              }}
            />
          </>
        )}
      </Container>
      <NotesModal
        notes={orderWithNotes?.cart.notes ?? null}
        closeModal={() => setOrderWithNotes(null)}
        title={orderWithNotes?.reference ?? undefined}
      />
    </UIPage>
  );
};
